import { useEffect, useMemo, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTour } from '../../../shared/lib/TourContext';

const OVERLAY_COLOR = 'rgba(9, 9, 11, 0.62)';
const HIGHLIGHT_PADDING = 10;
const TOOLTIP_MAX_WIDTH = 360;
const VIEWPORT_MARGIN = 16;
const MOBILE_BREAKPOINT = 640;
const TOOLTIP_ESTIMATED_HEIGHT = 240;
const REF_SETTLE_MS = 400; // max time to wait for a ref to appear after navigation

const ProductTour = ({ onComplete, onSkip }) => {
  const { steps, isOpen } = useTour();
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [viewport, setViewport] = useState({ width: window.innerWidth, height: window.innerHeight });
  const location = useLocation();
  const navigate = useNavigate();

  const totalSteps = steps.length;
  const step = steps[currentStep];
  const hasTarget = !!targetRect;
  const isLastStep = currentStep === totalSteps - 1;

  // Reset when tour opens
  useEffect(() => {
    if (!isOpen) return;
    setCurrentStep(0);
    setTargetRect(null);
    if (steps[0]?.path && location.pathname !== steps[0].path) {
      navigate(steps[0].path, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Lock body scroll while open
  useEffect(() => {
    if (!isOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  // Try to measure the target ref — but give up quickly so the tooltip
  // always shows immediately. The spotlight is optional.
  useEffect(() => {
    if (!isOpen || !step) return undefined;

    setTargetRect(null);
    const startedAt = Date.now();
    let retryTimer = null;
    let alive = true;

    const measure = () => {
      if (!alive) return;

      setViewport({ width: window.innerWidth, height: window.innerHeight });

      const el = step.ref?.current;
      if (el) {
        const rect = el.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          setTargetRect({
            top: Math.max(VIEWPORT_MARGIN, rect.top - HIGHLIGHT_PADDING),
            left: Math.max(VIEWPORT_MARGIN, rect.left - HIGHLIGHT_PADDING),
            width: Math.min(window.innerWidth - VIEWPORT_MARGIN * 2, rect.width + HIGHLIGHT_PADDING * 2),
            height: Math.min(window.innerHeight - VIEWPORT_MARGIN * 2, rect.height + HIGHLIGHT_PADDING * 2),
          });
          return; // done — got a valid rect
        }
      }

      // Retry briefly so navigation has time to mount the component
      if (Date.now() - startedAt < REF_SETTLE_MS) {
        retryTimer = window.setTimeout(measure, 80);
      }
      // else: give up — tooltip will show centered, no spotlight
    };

    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);

    return () => {
      alive = false;
      if (retryTimer) window.clearTimeout(retryTimer);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [isOpen, currentStep, step]);

  const goToStep = useCallback((idx) => {
    const target = steps[idx];
    setTargetRect(null); // clear immediately so tooltip doesn't jump
    if (target?.path && location.pathname !== target.path) {
      navigate(target.path, { replace: true });
    }
    setCurrentStep(idx);
  }, [steps, location.pathname, navigate]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') { onSkip(); return; }
      if (e.key === 'ArrowRight') {
        if (isLastStep) onComplete();
        else goToStep(currentStep + 1);
        return;
      }
      if (e.key === 'ArrowLeft') {
        if (currentStep > 0) goToStep(currentStep - 1);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [currentStep, isLastStep, isOpen, onComplete, onSkip, goToStep]);

  const tooltipStyle = useMemo(() => {
    const panelWidth = Math.min(TOOLTIP_MAX_WIDTH, viewport.width - VIEWPORT_MARGIN * 2);

    if (viewport.width < MOBILE_BREAKPOINT) {
      return { left: VIEWPORT_MARGIN, right: VIEWPORT_MARGIN, bottom: VIEWPORT_MARGIN, width: 'auto' };
    }

    // No spotlight target — center the tooltip
    if (!hasTarget) {
      return { left: '50%', top: '50%', width: panelWidth, transform: 'translate(-50%, -50%)' };
    }

    // Position relative to the highlighted element
    let left = targetRect.left;
    if (left + panelWidth > viewport.width - VIEWPORT_MARGIN) left = viewport.width - panelWidth - VIEWPORT_MARGIN;
    if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN;

    let top = targetRect.top + targetRect.height + 16;
    if (top + TOOLTIP_ESTIMATED_HEIGHT > viewport.height - VIEWPORT_MARGIN) {
      top = Math.max(VIEWPORT_MARGIN, targetRect.top - TOOLTIP_ESTIMATED_HEIGHT - 16);
    }

    return { left, top, width: panelWidth };
  }, [hasTarget, targetRect, viewport.height, viewport.width]);

  if (!isOpen || totalSteps === 0 || !step) return null;

  return createPortal(
    <div className="fixed inset-0 z-[120]" aria-live="polite">
      {/* Overlay — spotlight cutout if we have a target, full dim otherwise */}
      {hasTarget ? (
        <div
          className="fixed rounded-xl border-2 border-white/80 pointer-events-none"
          style={{
            top: targetRect.top,
            left: targetRect.left,
            width: targetRect.width,
            height: targetRect.height,
            boxShadow: `0 0 0 9999px ${OVERLAY_COLOR}`,
          }}
        />
      ) : (
        <div className="fixed inset-0 bg-zinc-950/60" />
      )}

      {/* Tooltip — always visible */}
      <div
        role="dialog"
        aria-modal="true"
        className="fixed rounded-xl border border-zinc-200 bg-white p-5 shadow-2xl"
        style={tooltipStyle}
      >
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">
            Step {currentStep + 1} / {totalSteps}
          </p>
          <button onClick={onSkip} className="text-xs text-zinc-400 hover:text-zinc-600 transition-colors">
            Skip
          </button>
        </div>

        <h3 className="text-lg font-medium text-zinc-900 mb-2">{step.title}</h3>
        <p className="text-sm text-zinc-600 leading-relaxed mb-5">{step.description}</p>

        <div className="flex items-center justify-between gap-3">
          <button
            onClick={() => currentStep > 0 && goToStep(currentStep - 1)}
            disabled={currentStep === 0}
            className="px-3 py-2 text-sm border border-zinc-200 text-zinc-700 rounded-md hover:border-zinc-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Back
          </button>
          <button
            onClick={() => isLastStep ? onComplete() : goToStep(currentStep + 1)}
            className="px-4 py-2 text-sm bg-zinc-900 text-white rounded-md hover:bg-zinc-800 transition-colors"
          >
            {isLastStep ? 'Finish' : 'Next'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ProductTour;
